import { panelHeaderControlClass } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { OkthxLogo } from "@/components/icons/Logo.tsx";
import { cn } from "@/lib/utils.ts";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";

/**
 * Collapses and reopens the left sidebar, as the same two-panel glyph the thread
 * header's canvas toggle uses.
 *
 * One component for both seats the control takes — the sidebar's own header
 * while it is open, the canvas header once the sidebar collapses to nothing —
 * so the two cannot drift in glyph, label, or metric.
 *
 * Read the chevron, not the icon's suffix: `PanelLeftClose` points left (the
 * column retreats), `PanelLeftOpen` points right (it grows), and the label names
 * the action rather than the state.
 *
 * The collapsed seat rests as the okthx disc: collapsing takes the wordmark off
 * the screen, so this is the only brand mark left. The control glyph on hover
 * keeps the corner a logo at rest and still puts the way back under the
 * pointer.
 */
export const SidebarToggleButton = ({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) => {
  const label = open ? "Collapse sidebar" : "Open sidebar";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            panelHeaderControlClass,
            // Collapsed, this is the only mark on the canvas header, so it rests
            // as the brand and only becomes a control under the pointer. Round
            // rather than the usual square hover, so the fill reads as a disc
            // behind a disc.
            //
            // It wears the shared class in both seats now that the class carries
            // no resting opacity — a brand mark must not have one, and restating
            // the box and glyph sizes here to avoid it is how the two seats came
            // to read at different scales once before.
            !open && "group relative rounded-full p-0",
          )}
          aria-label={label}
          aria-expanded={open}
          data-statsig-name="nav-sidebar-toggle"
          onClick={onToggle}
        >
          {open ? (
            <PanelLeftClose />
          ) : (
            <>
              {/* Both layers stay mounted and cross-fade in place: swapping the
                  element on hover would re-layout the header line under the
                  pointer, and the two glyphs are different widths. */}
              {/* Both sized by the wrapper above, so the mark does not appear to
                  change size under the pointer — only the fill arrives. */}
              <OkthxLogo className="transition-opacity group-hover:opacity-0" />
              <PanelLeftOpen className="absolute opacity-0 transition-opacity group-hover:opacity-100" />
            </>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
};
